/**
 * Candidate "Récap 360°" page — single screen with everything you need
 * to know about a candidate at a glance.
 *
 * Layout inspired by the Shariti V2 Excel scorecard, but driven entirely
 * by real data (no hardcoded values).
 *
 * Keyboard:
 *   ← / → : navigate the call queue if any
 *   Esc   : back to the candidate profile
 */
import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    ClipboardCheck,
    FileDown,
    Flame,
    Loader2,
    Lock,
    MapPin,
    Phone,
    ShieldCheck,
    Sparkles,
    Target,
    User,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getQueuePosition } from "../lib/callQueue";
import { PROFILE_DOMINANT_LABELS, STATUS_META } from "../lib/constants";
import { goBack } from "../lib/navigation";
import { hasRole } from "../lib/roles";

const DECISION_TONE = {
    "Recrutement prioritaire": { tone: "emerald", icon: CheckCircle2 },
    "Recrutement possible": { tone: "amber", icon: CheckCircle2 },
    "Réserve / À surveiller": { tone: "amber", icon: AlertTriangle },
    "Refus recommandé": { tone: "red", icon: AlertTriangle },
};

const TONE_CLASSES = {
    emerald: { card: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", num: "text-emerald-600" },
    amber: { card: "bg-amber-50 border-amber-200", text: "text-amber-700", num: "text-amber-600" },
    red: { card: "bg-red-50 border-red-200", text: "text-shariti-red", num: "text-shariti-red" },
    orange: { card: "bg-shariti-orange-soft border-shariti-orange/30", text: "text-shariti-orange", num: "text-shariti-orange" },
    slate: { card: "bg-shariti-cream border-shariti-gray", text: "text-slate-600", num: "text-shariti-black" },
};

function NoteBadge({ note }) {
    if (note == null) return <span className="text-slate-300 text-xs font-bold">—</span>;
    const tone = note <= 2 ? "bg-red-50 text-shariti-red border-red-200"
        : note === 3 ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-emerald-50 text-emerald-700 border-emerald-200";
    return (
        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border-2 text-sm font-extrabold ${tone}`}>
            {note}
        </span>
    );
}

function decisionFromPercent(pct) {
    if (pct >= 85) return "Recrutement prioritaire";
    if (pct >= 70) return "Recrutement possible";
    if (pct >= 55) return "Réserve / À surveiller";
    return "Refus recommandé";
}

export default function CandidateRecap() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [data, setData] = useState(null);
    const [criteria, setCriteria] = useState({ prerec: [], immersion: [] });
    const [loading, setLoading] = useState(true);
    const [accessError, setAccessError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setAccessError(null);
        try {
            const [{ data: candPayload }, { data: crits }] = await Promise.all([
                api.get(`/candidates/${id}`),
                api.get("/candidates/criteria"),
            ]);
            setData(candPayload);
            setCriteria(crits);
        } catch (e) {
            if (e.response?.status === 403) {
                setAccessError({ code: 403, detail: e.response?.data?.detail });
            } else if (e.response?.status === 404) {
                setAccessError({ code: 404, detail: "Candidat introuvable" });
            } else {
                setAccessError({ code: 0, detail: "Erreur de chargement" });
            }
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const queueInfo = useMemo(() => getQueuePosition(id), [id]);

    // Keyboard navigation
    useEffect(() => {
        function handleKey(e) {
            if (e.target?.tagName?.toLowerCase() === "input" || e.target?.tagName?.toLowerCase() === "textarea") return;
            if (e.key === "ArrowLeft" && queueInfo?.prevId) {
                navigate(`/candidats/${queueInfo.prevId}/recap`);
            } else if (e.key === "ArrowRight" && queueInfo?.nextId) {
                navigate(`/candidats/${queueInfo.nextId}/recap`);
            } else if (e.key === "Escape") {
                navigate(`/candidats/${id}`);
            }
        }
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [navigate, id, queueInfo]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    if (accessError) {
        return (
            <div className="card-soft p-10 text-center max-w-lg mx-auto mt-12" data-testid="recap-access-error">
                <div className="h-14 w-14 rounded-2xl bg-red-50 text-shariti-red mx-auto inline-flex items-center justify-center">
                    <Lock className="h-7 w-7" />
                </div>
                <h2 className="mt-4 text-2xl font-black tracking-tight text-shariti-black">
                    {accessError.code === 403 ? "Hors de votre périmètre" : "Candidat indisponible"}
                </h2>
                <p className="mt-2 text-sm font-medium text-slate-500">{accessError.detail}</p>
                <button onClick={() => goBack(navigate)} className="mt-6 btn-shariti">
                    <ArrowLeft className="h-4 w-4" />
                    Retour à la liste
                </button>
            </div>
        );
    }

    if (!data) return null;

    const c = data.candidate;
    const sub = c.sub_scores || {};
    const subDetail = c.ai_analysis?.sub_scores_detail || {};
    const valued = c.ai_analysis?.valued_signals || [];
    const isReferent = hasRole(user, "referent");
    const isPreRecruteur = hasRole(user, "pre_recruteur");

    // Find the most recent prerec & immersion scorecards
    const prerecCard = (data.scorecards || []).filter((s) => s.type === "prerec").sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    const immersionCard = (data.scorecards || []).filter((s) => s.type === "immersion").sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    const showPrerec = !isReferent;
    const showImmersion = !isPreRecruteur;

    const prerecPct = prerecCard?.max_score ? Math.round((prerecCard.total_score / prerecCard.max_score) * 100) : null;
    const immersionPct = immersionCard?.max_score ? Math.round((immersionCard.total_score / immersionCard.max_score) * 100) : null;

    const globalScore = (() => {
        const parts = [];
        if (prerecPct != null) parts.push(prerecPct);
        if (immersionPct != null) parts.push(immersionPct);
        if (parts.length === 0) return c.ai_score || null;
        const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
        return Math.round(avg);
    })();

    const globalDecision = globalScore != null ? decisionFromPercent(globalScore) : "—";
    const decisionMeta = DECISION_TONE[globalDecision] || DECISION_TONE["Réserve / À surveiller"];
    const decisionTone = TONE_CLASSES[decisionMeta.tone];
    const DecisionIcon = decisionMeta.icon;

    const ratingsByCriterion = {
        prerec: Object.fromEntries((prerecCard?.ratings || []).map((r) => [r.criterion_id, r])),
        immersion: Object.fromEntries((immersionCard?.ratings || []).map((r) => [r.criterion_id, r])),
    };

    return (
        <div className="space-y-5" data-testid="candidate-recap-page">
            {/* TOP BAR — Sticky : queue navigation + back + CV */}
            <div className="sticky top-16 z-20 -mt-6 mb-2 mx-[-1rem] md:mx-[-2rem] bg-shariti-cream/95 backdrop-blur-xl border-b border-shariti-gray">
                {queueInfo && (
                    <div className="px-4 md:px-8 py-2 flex flex-wrap items-center gap-3 bg-shariti-orange-soft/40 border-b border-shariti-orange/15">
                        <ClipboardCheck className="h-4 w-4 text-shariti-orange shrink-0" />
                        <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange truncate">
                            Récap 360° · {queueInfo.label}
                        </div>
                        <div className="text-xs font-extrabold text-shariti-black">
                            Candidat {queueInfo.position} / {queueInfo.total}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={() => queueInfo.prevId && navigate(`/candidats/${queueInfo.prevId}/recap`)}
                                disabled={!queueInfo.prevId}
                                data-testid="recap-prev"
                                title="Candidat précédent (←)"
                                className="h-8 px-3 inline-flex items-center gap-1 rounded-full bg-white border border-shariti-orange/30 text-shariti-orange hover:bg-shariti-orange hover:text-white transition disabled:opacity-30 text-[11px] font-extrabold"
                            >
                                <ChevronLeft className="h-3.5 w-3.5" />
                                Précédent
                            </button>
                            <button
                                onClick={() => queueInfo.nextId && navigate(`/candidats/${queueInfo.nextId}/recap`)}
                                disabled={!queueInfo.nextId}
                                data-testid="recap-next"
                                title="Candidat suivant (→)"
                                className="h-8 px-3 inline-flex items-center gap-1 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition disabled:opacity-30 text-[11px] font-extrabold shadow-btn"
                            >
                                Suivant
                                <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                )}
                <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => navigate(`/candidats/${id}`)}
                        className="h-9 px-3 inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-500 hover:text-shariti-orange hover:border-shariti-orange transition text-xs font-bold"
                        title="Retour fiche (Esc)"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Fiche détaillée
                    </button>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Navigation clavier ← / → · Esc pour retour
                    </div>
                    {c.cv_url && (
                        <a
                            href={c.cv_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="recap-cv-button"
                            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-shariti-black text-white hover:bg-slate-800 px-3 h-9 text-[11px] font-extrabold transition shadow-sm"
                        >
                            <FileDown className="h-3.5 w-3.5" />
                            CV PDF
                        </a>
                    )}
                </div>
            </div>

            {/* HERO — Big synthesis card inspired by Shariti V2 Excel */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-soft overflow-hidden p-0"
                data-testid="recap-hero"
            >
                {/* Header orange */}
                <div className="bg-gradient-to-r from-shariti-orange to-[#d66528] text-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        {c.avatar_url ? (
                            <img src={c.avatar_url} alt="" className="h-12 w-12 rounded-2xl object-cover shrink-0 border-2 border-white/40" />
                        ) : (
                            <div className="h-12 w-12 rounded-2xl bg-white/20 text-white font-black text-xl flex items-center justify-center shrink-0">
                                {c.first_name?.[0]}
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-white/80">
                                Récap 360° du candidat
                            </div>
                            <div className="text-xl md:text-2xl font-black tracking-tight truncate">
                                {c.first_name} {c.last_name}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {(c.ai_score || 0) >= 82 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-shariti-yellow text-shariti-black">
                                <Flame className="h-3 w-3" /> Pépite
                            </span>
                        )}
                        {c.risk_level === "high" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-50 text-shariti-red">
                                <AlertTriangle className="h-3 w-3" /> Risque
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/20 text-white">
                            {STATUS_META[c.status]?.label || c.status}
                        </span>
                    </div>
                </div>

                {/* Body grid : 3 columns — Infos / Synthèse scores / Interprétation */}
                <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_220px] gap-0 divide-x divide-shariti-gray">
                    {/* LEFT — Infos candidat */}
                    <div className="p-5">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-3">
                            Informations
                        </div>
                        <dl className="space-y-2.5 text-xs">
                            <RecapField label="Nom" value={`${c.first_name} ${c.last_name}`} />
                            <RecapField label="Téléphone" value={c.phone || "—"} />
                            <RecapField label="Email" value={c.email || "—"} />
                            <RecapField label="Ville" value={c.city || "—"} />
                            <RecapField label="Source" value={c.layan_offer_title || "Manuel"} />
                            <RecapField label="Profil dominant" value={PROFILE_DOMINANT_LABELS[c.profile_dominant] || "—"} />
                            <RecapField label="Statut actuel" value={STATUS_META[c.status]?.label || c.status} />
                        </dl>
                    </div>

                    {/* CENTER — 3 score boxes (prerec / immersion / global) + decision */}
                    <div className="p-5">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-3 text-center">
                            Synthèse automatisée
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {showPrerec && (
                                <ScoreCard
                                    label="Pré-recrutement"
                                    score={prerecPct}
                                    tone="orange"
                                    testid="recap-score-prerec"
                                />
                            )}
                            {showImmersion && (
                                <ScoreCard
                                    label="Immersion terrain"
                                    score={immersionPct}
                                    tone="orange"
                                    testid="recap-score-immersion"
                                />
                            )}
                            <ScoreCard
                                label="Score global"
                                score={globalScore}
                                tone="slate"
                                testid="recap-score-global"
                                big
                            />
                        </div>
                        {/* Decision banner */}
                        <div className={`mt-4 rounded-2xl border ${decisionTone.card} px-4 py-3 flex items-center gap-3`} data-testid="recap-decision">
                            <DecisionIcon className={`h-6 w-6 ${decisionTone.num}`} />
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Décision recommandée
                                </div>
                                <div className={`text-base font-black tracking-tight ${decisionTone.text}`}>
                                    {globalDecision}
                                </div>
                            </div>
                        </div>
                        {/* Sub indicators */}
                        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                            <MiniIndicator
                                icon={Target}
                                label="Potentiel long terme"
                                value={
                                    (sub.potentiel_long_terme || 0) >= 70 ? "ÉLEVÉ"
                                        : (sub.potentiel_long_terme || 0) >= 50 ? "MOYEN" : "FAIBLE"
                                }
                                tone={(sub.potentiel_long_terme || 0) >= 70 ? "emerald" : "amber"}
                            />
                            <MiniIndicator
                                icon={ShieldCheck}
                                label="Niveau de risque"
                                value={c.risk_level === "low" ? "FAIBLE" : c.risk_level === "high" ? "ÉLEVÉ" : "MOYEN"}
                                tone={c.risk_level === "low" ? "emerald" : c.risk_level === "high" ? "red" : "amber"}
                            />
                            <MiniIndicator
                                icon={User}
                                label="Profil dominant"
                                value={PROFILE_DOMINANT_LABELS[c.profile_dominant] || "—"}
                                tone="slate"
                            />
                        </div>
                    </div>

                    {/* RIGHT — Interpretation legend */}
                    <div className="p-5 bg-shariti-cream/40">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-3 text-center">
                            Interprétation
                        </div>
                        <ul className="space-y-2 text-xs">
                            <LegendItem range="> 85" label="Recrutement prioritaire" tone="emerald" />
                            <LegendItem range="70 à 85" label="Recrutement possible" tone="amber" />
                            <LegendItem range="55 à 70" label="Réserve / surveiller" tone="amber" />
                            <LegendItem range="< 55" label="Refus" tone="red" />
                        </ul>
                        <div className="mt-4 rounded-2xl bg-shariti-black text-white px-3 py-2 text-center">
                            <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                                Notation
                            </div>
                            <div className="text-xs font-bold">
                                1 (Faible) → 5 (Excellent)
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* TWO SCORECARDS SIDE BY SIDE */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {showPrerec && (
                    <ScorecardTable
                        title="1. Pré-recrutement (téléphone)"
                        criteriaList={criteria.prerec}
                        ratingsByCriterion={ratingsByCriterion.prerec}
                        scorecard={prerecCard}
                        emptyHint="Aucune scorecard pré-rec encore enregistrée."
                        testid="recap-table-prerec"
                    />
                )}
                {showImmersion && (
                    <ScorecardTable
                        title="2. Immersion terrain (référent)"
                        criteriaList={criteria.immersion}
                        ratingsByCriterion={ratingsByCriterion.immersion}
                        scorecard={immersionCard}
                        emptyHint="Aucune scorecard immersion encore enregistrée."
                        testid="recap-table-immersion"
                    />
                )}
            </div>

            {/* BOTTOM — Red flags / Risk / Performance / Forces / Vigilance */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                <RedFlagsCard ratingsByCriterion={ratingsByCriterion} criteria={criteria} testid="recap-red-flags" />
                <RiskCard label="Risque d'attrition" value={sub.risque_attrition || 0} highIsBad testid="recap-risk-attrition" />
                <RiskCard label="Potentiel de performance" value={Math.round(((sub.communication || 0) + (sub.persuasion || 0) + (sub.energie || 0)) / 3)} testid="recap-perf-potential" />
                <ForcesVigilanceCard
                    strengths={c.ai_analysis?.strengths || []}
                    vigilance={c.ai_analysis?.vigilance || []}
                    valuedSignals={valued}
                    detail={subDetail}
                    showVigilance={!isReferent}
                    testid="recap-forces"
                />
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────── sub-components

function RecapField({ label, value }) {
    return (
        <div className="flex justify-between gap-2 items-baseline">
            <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-400 shrink-0">{label}</dt>
            <dd className="text-xs font-extrabold text-shariti-black text-right break-words min-w-0">{value}</dd>
        </div>
    );
}

function ScoreCard({ label, score, tone = "orange", testid, big = false }) {
    const t = TONE_CLASSES[tone];
    return (
        <div className={`rounded-2xl border ${t.card} px-4 py-3 text-center`} data-testid={testid}>
            <div className={`text-[10px] font-bold uppercase tracking-widest ${t.text}`}>{label}</div>
            <div className={`mt-1 ${big ? "text-4xl" : "text-3xl"} font-black tabular-nums ${t.num}`}>
                {score != null ? score : "—"}
                <span className="text-[11px] font-bold text-slate-400 ml-0.5">/100</span>
            </div>
        </div>
    );
}

function MiniIndicator({ icon: Icon, label, value, tone }) {
    const t = TONE_CLASSES[tone] || TONE_CLASSES.slate;
    return (
        <div className="text-center">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</div>
            <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${t.card} ${t.text} text-[10px] font-extrabold`}>
                <Icon className="h-3 w-3" />
                {value}
            </div>
        </div>
    );
}

function LegendItem({ range, label, tone }) {
    const t = TONE_CLASSES[tone] || TONE_CLASSES.slate;
    return (
        <li className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-shariti-red"}`} />
            <span className="font-bold text-slate-700">{range}</span>
            <span className={`ml-auto text-[11px] font-bold ${t.text}`}>{label}</span>
        </li>
    );
}

function ScorecardTable({ title, criteriaList, ratingsByCriterion, scorecard, emptyHint, testid }) {
    const hasRatings = Object.keys(ratingsByCriterion).length > 0;
    return (
        <div className="card-soft p-0 overflow-hidden" data-testid={testid}>
            <div className="bg-shariti-orange text-white px-4 py-2.5 flex items-center justify-between">
                <h3 className="text-sm font-extrabold uppercase tracking-widest">{title}</h3>
                {scorecard?.total_score != null && scorecard?.max_score && (
                    <div className="text-[10px] font-bold">
                        TOTAL : {scorecard.total_score.toFixed?.(1) ?? scorecard.total_score} / {scorecard.max_score.toFixed?.(1) ?? scorecard.max_score}
                    </div>
                )}
            </div>
            {!hasRatings ? (
                <div className="p-6 text-center text-xs font-medium text-slate-500">
                    {emptyHint}
                </div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="bg-shariti-cream text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        <tr>
                            <th className="text-left px-3 py-2">Critère</th>
                            <th className="text-center px-2 py-2 w-12">Note</th>
                            <th className="text-right px-2 py-2 w-16">Score</th>
                            <th className="text-left px-3 py-2">Commentaire</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-shariti-gray">
                        {criteriaList.map((crit) => {
                            const r = ratingsByCriterion[crit.id];
                            const note = r?.note;
                            const score = note ? (note * crit.weight) : null;
                            return (
                                <tr key={crit.id} className="hover:bg-shariti-cream/40 transition">
                                    <td className="px-3 py-2 align-top">
                                        <div className="font-extrabold text-shariti-black">{crit.label}</div>
                                        <div className="text-[10px] text-slate-400 font-medium">×{crit.weight}</div>
                                    </td>
                                    <td className="text-center px-2 py-2 align-top">
                                        <NoteBadge note={note} />
                                    </td>
                                    <td className="text-right px-2 py-2 align-top font-bold text-shariti-black tabular-nums">
                                        {score != null ? score.toFixed(1) : "—"}
                                    </td>
                                    <td className="px-3 py-2 align-top font-medium text-slate-600 leading-snug">
                                        {r?.comment || "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {scorecard?.decision && (
                <div className="px-4 py-2.5 bg-shariti-cream/40 border-t border-shariti-gray flex items-center gap-2 text-xs">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="font-extrabold text-shariti-black">Décision automatisée :</span>
                    <span className="font-bold text-shariti-orange">{scorecard.decision}</span>
                </div>
            )}
            {scorecard?.general_comment && (
                <div className="px-4 py-2.5 border-t border-shariti-gray text-xs">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                        Commentaire général
                    </div>
                    <div className="text-xs font-medium text-slate-700">{scorecard.general_comment}</div>
                </div>
            )}
        </div>
    );
}

function RedFlagsCard({ ratingsByCriterion, criteria, testid }) {
    // Build red flag list: any criterion with rating ≤ 2 across both scorecards
    const flags = [];
    [...criteria.prerec, ...criteria.immersion].forEach((c) => {
        const pr = ratingsByCriterion.prerec[c.id];
        const im = ratingsByCriterion.immersion[c.id];
        if (pr?.note && pr.note <= 2) flags.push({ label: c.label, note: pr.note, source: "Pré-rec" });
        if (im?.note && im.note <= 2) flags.push({ label: c.label, note: im.note, source: "Immersion" });
    });
    return (
        <div className="card-soft p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-shariti-red" />
                <h3 className="text-xs font-extrabold uppercase tracking-widest text-shariti-red">
                    Red flags ({flags.length})
                </h3>
            </div>
            {flags.length === 0 ? (
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Aucune alerte critique
                </div>
            ) : (
                <ul className="space-y-2 text-xs">
                    {flags.slice(0, 6).map((f, i) => (
                        <li key={i} className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-shariti-red shrink-0" />
                            <span className="font-bold text-shariti-black flex-1">{f.label}</span>
                            <span className="text-[10px] font-bold text-slate-400">{f.source}</span>
                            <NoteBadge note={f.note} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function RiskCard({ label, value, highIsBad = false, testid }) {
    const ok = highIsBad ? value < 40 : value >= 70;
    const warn = highIsBad ? value < 60 : value >= 50;
    const tone = ok ? "emerald" : warn ? "amber" : "red";
    const t = TONE_CLASSES[tone];
    const ico = highIsBad ? (
        ok ? <ShieldCheck className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />
    ) : (
        ok ? <Sparkles className="h-7 w-7" /> : <Target className="h-7 w-7" />
    );
    return (
        <div className={`card-soft p-5 text-center ${t.card}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">{label}</div>
            <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-white ${t.text} mb-2`}>
                {ico}
            </div>
            <div className={`text-2xl font-black ${t.num}`}>
                {ok ? (highIsBad ? "FAIBLE" : "ÉLEVÉ") : warn ? "MOYEN" : (highIsBad ? "ÉLEVÉ" : "FAIBLE")}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                ({value}%)
            </div>
        </div>
    );
}

function ForcesVigilanceCard({ strengths, vigilance, valuedSignals, detail, showVigilance, testid }) {
    return (
        <div className="card-soft p-5 col-span-1 md:col-span-2 xl:col-span-1" data-testid={testid}>
            {valuedSignals.length > 0 && (
                <div className="mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">
                        ✨ Signaux Shariti
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {valuedSignals.map((s, i) => (
                            <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700">
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {strengths.length > 0 && (
                <div className="mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">
                        Forces
                    </div>
                    <ul className="space-y-1 text-xs font-medium text-slate-700">
                        {strengths.slice(0, 4).map((s, i) => (
                            <li key={i} className="flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />{s}</li>
                        ))}
                    </ul>
                </div>
            )}
            {showVigilance && vigilance.length > 0 && (
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1.5">
                        Vigilance
                    </div>
                    <ul className="space-y-1 text-xs font-medium text-slate-700">
                        {vigilance.slice(0, 4).map((s, i) => (
                            <li key={i} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />{s}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
